import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useServiceLayerAuth } from './ServiceLayerAuthProvider';
import { authClient } from './authClient';
import { ensureUserProfileFromLogin, listUsers, mapDomainRoleToUiRole, type DomainRole } from '../api/users';
import { productionTeamMembers } from '@appic/data';
import { trackEvent } from '../events';

export type Role = 'Admin' | 'Sales' | 'Viewer' | 'Manager';

export type CurrentUser = {
	id: string | null;
	email: string | null;
	role: Role;
	impersonating?: { email: string; fullName?: string | null } | null;
};

const UserContext = createContext<CurrentUser>({ id: null, email: null, role: 'Viewer' });

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { email: serviceEmail, role: serviceRole } = useServiceLayerAuth();
	const env = import.meta.env;
	const TEST_MODE = env.VITE_TEST_MODE === 'true' || env.VITE_MOCK_AUTH === 'true';
	const [impersonated, setImpersonated] = useState<{ email: string; fullName?: string | null } | null>(() => {
		try {
			const raw = sessionStorage.getItem('scout.impersonation.v1');
			return raw ? JSON.parse(raw) : null;
		} catch {
			return null;
		}
	});

	// Default role before server profile is resolved
	const initialRole = useMemo(() => 'Viewer', []);

	const [resolved, setResolved] = useState<CurrentUser>(() => ({
		id: null,
		email: serviceEmail || null,
		role: initialRole as Role,
		impersonating: impersonated,
	}));

	useEffect(() => {
		let cancel = false;
		async function run() {
			// If we have an account email, map role to UI role
			const email = (impersonated?.email || serviceEmail) || null;
			if (!email) {
				if (!cancel) setResolved({ id: null, email: null, role: initialRole as Role, impersonating: null });
				return;
			}

			// Resolve role: prefer JWT session role (immediate), fall back to profile API
			let uiRole: Role = initialRole as Role;
			if (serviceRole && !impersonated) {
				uiRole = mapDomainRoleToUiRole(serviceRole as DomainRole) as Role;
			}

			// caller_id must be the AUTHENTICATED user's id (auth.uid). Resolve it
			// from the live session so a drifted login email can't land us on a
			// duplicate users row (BUG-SL-RUEDIGER-RLS-USERS-WRONG-ROW-DUP-ACCOUNTS):
			// the uid is the canonical public.users PK. NOT under impersonation —
			// there the impersonated identity resolves by email, never the admin's uid.
			let authUserId: string | null = null;
			if (!impersonated) {
				try {
					const session = await authClient.getSession();
					authUserId = session.user?.id ?? null;
				} catch {
					authUserId = null;
				}
			}

			// Always fetch profile to get user ID (needed for caller assignment, etc.)
			try {
				const profile = await ensureUserProfileFromLogin(email, impersonated?.fullName || null, authUserId);
				if (!serviceRole || impersonated) {
					uiRole = mapDomainRoleToUiRole(profile.role) as Role;
				}
				// Test-only override for E2E: allow role injection via window.__TEST_ROLE__
				try {
					if (TEST_MODE && (window as Window & { __TEST_ROLE__?: string }).__TEST_ROLE__) {
						const r = String((window as Window & { __TEST_ROLE__?: string }).__TEST_ROLE__);
						if (r === 'Admin' || r === 'Sales' || r === 'Viewer' || r === 'Manager') uiRole = r as Role;
					}
				} catch { /* noop */ }
				if (!cancel) setResolved({ id: profile.id, email, role: uiRole, impersonating: impersonated });
			} catch (error) {
				console.error('[UserContext] Failed to resolve user profile:', error, { email });
				const teamMember = productionTeamMembers.find(m => m.email.toLowerCase() === email.toLowerCase());
				const fallbackRole = teamMember ? mapDomainRoleToUiRole(teamMember.role) : 'Sales';
				if (!cancel) setResolved({ id: null, email, role: fallbackRole as Role, impersonating: impersonated });
			}
		}
		void run();
		return () => { cancel = true; };
	}, [serviceEmail, serviceRole, initialRole, impersonated, TEST_MODE]);

	// Warm user cache from API when authenticated
	useEffect(() => {
		if (serviceEmail) {
			listUsers().catch(() => { /* fallback to productionTeamMembers */ });
		}
	}, [serviceEmail]);

	// Guest mode removed: do not auto-impersonate

	// Global helpers as a minimal bridge for UI controls (avoids prop drilling)
	// Type for window extensions used by this module
	type WindowWithTestHooks = Window & {
		__IMPERSONATION__?: { start: (email: string) => void; stop: () => void };
		__MOCK_USER_CONTEXT__?: (context: { email?: string; role?: string } | null) => void;
	};

	useEffect(() => {
		(window as WindowWithTestHooks).__IMPERSONATION__ = {
			start: (email: string) => {
				const found = productionTeamMembers.find((m) => m.email.toLowerCase() === email.toLowerCase());
				const payload = { email, fullName: found?.name ?? null };
				setImpersonated(payload);
				try { sessionStorage.setItem('scout.impersonation.v1', JSON.stringify(payload)); } catch { /* noop */ }
				trackEvent('impersonation_start', { email });
			},
			stop: () => {
				setImpersonated(null);
				try { sessionStorage.removeItem('scout.impersonation.v1'); } catch { /* noop */ }
				trackEvent('impersonation_stop');
			},
		};
		// Test-only hook: allow E2E to mock user context directly
		if (TEST_MODE) {
			(window as WindowWithTestHooks).__MOCK_USER_CONTEXT__ = (context) => {
				if (!context) { setResolved({ id: null, email: null, role: 'Viewer', impersonating: null }); return; }
				const email = context.email || 'test@clanda.de';
				const role = (context.role === 'Admin' || context.role === 'Sales' || context.role === 'Viewer' || context.role === 'Manager') ? context.role : 'Sales';
				setResolved({ id: 'test-user-id', email, role, impersonating: null });
			};
		}
	}, [TEST_MODE]);

	const value = useMemo<CurrentUser>(() => resolved, [resolved]);
	return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export function useCurrentUser(): CurrentUser {
	return useContext(UserContext);
}
